import React, { useState, useMemo } from 'react';
import awsLogo from '../assets/aws.png';

const Skills = () => {
  const [activeCategory, setActiveCategory] = useState('Frontend');

  // Logo URL helper function - using colored logos from devicons or similar
  const getLogoUrl = (name) => {
    // Using devicons.dev which provides colored logos
    const logoMap = {
      'React': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg',
      'TypeScript': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/typescript/typescript-original.svg',
      'JavaScript': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg',
      'HTML/CSS': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/css3/css3-original.svg',
      'TailwindCSS': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/tailwindcss/tailwindcss-original.svg',
      'Next.js': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nextjs/nextjs-original.svg',
      'Web Accessibility': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/html5/html5-original.svg',
      'Node.js': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg',
      'Express': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/express/express-original.svg',
      'MongoDB': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg',
      'PostgreSQL': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postgresql/postgresql-original.svg',
      'Firebase': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/firebase/firebase-plain.svg',
      'Python': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg',
      'Git/GitHub': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/github/github-original.svg',
      'Testing': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/jest/jest-plain.svg',
      'CI/CD': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/githubactions/githubactions-original.svg',
      'Docker': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg',
      'AWS': awsLogo,
      'Figma': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/figma/figma-original.svg',
      'Webpack': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/webpack/webpack-original.svg',
      'UI/UX Design': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/figma/figma-original.svg',
      'Responsive Design': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/css3/css3-original.svg',
      'Animation': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/css3/css3-original.svg',
      'Prototyping': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/figma/figma-original.svg',
      'Design Systems': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/storybook/storybook-original.svg'
    };
    
    // Handle special cases
    if (name.includes('HTML') || name.includes('CSS')) return logoMap['HTML/CSS'];
    if (name.includes('Git') || name.includes('GitHub')) return logoMap['Git/GitHub'];
    if (name.includes('UI/UX') || name.includes('Design')) return logoMap['UI/UX Design'] || logoMap['Figma'];
    
    return logoMap[name] || null;
  };

  const skillsData = useMemo(() => ({
    Frontend: [
      { name: 'React', level: 95 },
      { name: 'TypeScript', level: 92 },
      { name: 'JavaScript', level: 95 },
      { name: 'HTML/CSS', level: 96 },
      { name: 'TailwindCSS', level: 94 },
      { name: 'Next.js', level: 88 },
      { name: 'Web Accessibility', level: 87 }
    ],
    Backend: [
      { name: 'Node.js', level: 88 },
      { name: 'Express', level: 85 },
      { name: 'MongoDB', level: 85 },
      { name: 'PostgreSQL', level: 80 },
      { name: 'Firebase', level: 82 },
      { name: 'Python', level: 75 }
    ],
    Tools: [
      { name: 'Git/GitHub', level: 94 },
      { name: 'Testing', level: 87 },
      { name: 'CI/CD', level: 86 },
      { name: 'Docker', level: 78 },
      { name: 'AWS', level: 80 },
      { name: 'Figma', level: 85 },
      { name: 'Webpack', level: 82 }
    ],
    Design: [
      { name: 'UI/UX Design', level: 88 },
      { name: 'Responsive Design', level: 95 },
      { name: 'Animation', level: 85 },
      { name: 'Prototyping', level: 84 },
      { name: 'Design Systems', level: 87 }
    ]
  }), []);

  const categoryIcons = {
    Frontend: '🎨',
    Backend: '⚙️',
    Tools: '🔧',
    Design: '🖌️'
  };

  const getSkillLabel = (level) => {
    if (level >= 90) return 'EXPERT';
    if (level >= 85) return 'ADVANCED';
    return 'PROFICIENT';
  };

  return (
    <section id="skills" className="skills-section">
      <div className="skills-container">
        <div className="section-header">
          <h2 className="section-title">Technical Skills</h2>
          <p className="section-subtitle">
            Comprehensive expertise across modern web technologies, frameworks, and development tools
          </p>
        </div>

        <div className="filter-buttons">
          {Object.keys(skillsData).map(category => (
            <button
              key={category}
              className={`filter-btn ${activeCategory === category ? 'active' : ''}`}
              onClick={() => setActiveCategory(category)}
              aria-pressed={activeCategory === category}
            >
              <span className="btn-icon">{categoryIcons[category]}</span>
              <span className="btn-label">{category}</span>
            </button>
          ))}
        </div>

        <div className="skills-grid">
          {skillsData[activeCategory].map((skill) => {
            const logoUrl = getLogoUrl(skill.name);
            return (
              <div key={skill.name} className="skill-card">
                <div className="skill-card-content">
                  <div className="skill-icon-wrapper">
                    {logoUrl ? (
                      <img 
                        src={logoUrl} 
                        alt={`${skill.name} logo`} 
                        className="skill-icon skill-logo"
                      />
                    ) : (
                      <span className="skill-icon skill-icon-fallback">{skill.name.charAt(0)}</span>
                    )}
                  </div>
                  <div className="skill-info">
                    <h3 className="skill-title">{skill.name}</h3>
                    <span className="skill-level-badge">{getSkillLabel(skill.level)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Skills;
